import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import CANDIDATE_SITES, { PLUME_DEFAULTS } from "./data/sites";
import SPECIES_DATA from "./data/species";
import TabIntro from "./components/TabIntro";
import DataWorkspace from "./DataWorkspace";
import DataSourcesSection from "./components/DataSourcesSection";
import AppFooter from "./components/AppFooter";
import ContactModal from "./components/ContactModal";
import FeedbackModal from "./components/FeedbackModal";
import OnboardingTour, { shouldAutoStartTour } from "./components/OnboardingTour";
import RegulationsPage from "./components/RegulationsPage";
import { PRIMARY_TABS, DATA_SUBTABS } from "./copy/tabCopy";

function matchPrimary(pathname) {
  if (pathname.startsWith("/sources")) return "sources";
  if (pathname.startsWith("/regulations")) return "regulations";
  if (pathname.startsWith("/data")) return "data";
  return "overview";
}

function matchDataSub(pathname) {
  const m = pathname.match(/^\/data\/([^/]+)/);
  if (m && DATA_SUBTABS.some((s) => s.id === m[1])) return m[1];
  return "sites";
}

const navBtn = (active) =>
  `flex shrink-0 items-center gap-2 rounded-ds px-3 py-2.5 text-xs font-medium transition-colors duration-fast focus:outline-none focus-visible:shadow-focus md:text-sm ${
    active ? "bg-surface text-fg shadow-card ring-1 ring-border" : "text-fg-secondary hover:bg-surface/80 hover:text-fg"
  }`;

const dataSubBtn = (active) =>
  `flex shrink-0 items-center gap-2 rounded-ds border px-3 py-2 text-xs transition-colors duration-fast focus:outline-none focus-visible:shadow-focus md:text-sm ${
    active ? "border-accent bg-accent-muted font-semibold text-fg" : "border-transparent bg-canvas-subtle text-fg-secondary hover:border-border hover:bg-canvas-muted"
  }`;

export default function GovernanceApp() {
  const location = useLocation();
  const navigate = useNavigate();

  const primary = matchPrimary(location.pathname);
  const dataSub = matchDataSub(location.pathname);

  const [selectedSite, setSelectedSite] = useState(CANDIDATE_SITES[0]);
  const [selectedSpecies, setSelectedSpecies] = useState(SPECIES_DATA[0]);
  const [plume, setPlume] = useState(PLUME_DEFAULTS);
  const [contactOpen, setContactOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);

  const updatePlume = (key, value) => setPlume((p) => ({ ...p, [key]: value }));

  const activePrimary = useMemo(() => PRIMARY_TABS.find((t) => t.id === primary) || PRIMARY_TABS[0], [primary]);
  const activeDataSub = useMemo(() => DATA_SUBTABS.find((s) => s.id === dataSub) || DATA_SUBTABS[0], [dataSub]);

  useEffect(() => {
    if (location.pathname === "/data") {
      navigate("/data/sites", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname === "/contact" || location.pathname.startsWith("/contact/")) {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (shouldAutoStartTour()) setTourRun(true);
  }, []);

  const goData = (subId) => {
    navigate(`/data/${subId}`);
  };

  const goRegulations = () => navigate("/regulations");

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-fg">
      <OnboardingTour run={tourRun} onDone={() => setTourRun(false)} />

      <header className="sticky top-0 z-50 border-b border-border bg-surface/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-content items-center gap-3 px-4 py-3 md:px-8">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ds-xl border border-border bg-canvas-subtle text-lg shadow-card" aria-hidden>
            🌊
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-bold tracking-tight text-fg md:text-base">Oceanic DC Governance</h1>
            <p className="hidden text-[11px] text-fg-tertiary sm:block">West Coast screening & regulation context</p>
          </div>
        </div>
        <nav className="border-t border-border bg-canvas-subtle px-2 md:px-6" aria-label="Primary sections">
          <div className="mx-auto flex max-w-content gap-1 overflow-x-auto py-1">
            {PRIMARY_TABS.map((t) => (
              <NavLink
                key={t.id}
                to={t.id === "data" ? "/data" : t.path}
                end={t.id === "overview"}
                data-tour={t.tourId}
                title={t.description}
                className={({ isActive }) => navBtn(isActive || (t.id === "data" && location.pathname.startsWith("/data")))}
              >
                <span aria-hidden>{t.icon}</span>
                <span className="whitespace-nowrap">{t.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
        {primary === "data" && (
          <div className="border-t border-border bg-surface px-2 md:px-6" data-tour="data-subnav" aria-label="Data tools">
            <div className="mx-auto flex max-w-content gap-1 overflow-x-auto py-2" role="tablist">
              {DATA_SUBTABS.map((s) => (
                <NavLink
                  key={s.id}
                  to={`/data/${s.id}`}
                  role="tab"
                  title={s.description}
                  className={({ isActive }) => dataSubBtn(isActive)}
                >
                  <span aria-hidden>{s.icon}</span>
                  {s.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main id="main-content" data-tour="app-main" className="mx-auto w-full max-w-content flex-1 px-4 py-6 transition-opacity duration-base md:px-8 md:py-8" tabIndex={-1}>
        {primary === "overview" && (
          <div className="animate-ds-fade-in">
            <TabIntro icon={activePrimary.icon} title={activePrimary.title} description={activePrimary.description} />
            <div className="mb-8 rounded-ds-xl border border-border bg-gradient-to-br from-canvas-subtle to-surface p-6 shadow-card md:p-8">
              <p className="mb-4 max-w-2xl text-sm text-fg-secondary">
                Jump into live environmental APIs and the regulations matrix. Data tools are under <strong className="font-medium text-fg">Data</strong>; binding thresholds and citations are under{" "}
                <strong className="font-medium text-fg">Regulations</strong>. Official API list: <NavLink to="/sources" className="font-medium text-accent hover:underline">Sources</NavLink>.
              </p>
              <div className="mb-4 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={goRegulations}
                  title={PRIMARY_TABS.find((t) => t.id === "regulations")?.description}
                  className="flex flex-col items-start rounded-ds-xl border border-accent/30 bg-accent-muted/40 p-4 text-left shadow-card transition-shadow duration-fast hover:shadow-card-hover focus:outline-none focus-visible:shadow-focus"
                >
                  <span className="mb-2 text-xl" aria-hidden>
                    ⚖
                  </span>
                  <span className="font-semibold text-fg">Regulations hub</span>
                  <span className="mt-1 text-xs text-fg-secondary">Search the full matrix, datacenter water context, and live SST for screening.</span>
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {DATA_SUBTABS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => goData(s.id)}
                    title={s.description}
                    className="flex flex-col items-start rounded-ds-xl border border-border bg-surface p-4 text-left shadow-card transition-shadow duration-fast hover:shadow-card-hover focus:outline-none focus-visible:shadow-focus"
                  >
                    <span className="mb-2 text-xl" aria-hidden>
                      {s.icon}
                    </span>
                    <span className="font-semibold text-fg">{s.title}</span>
                    <span className="mt-1 text-xs text-fg-secondary">{s.description}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {primary === "regulations" && (
          <div className="animate-ds-fade-in">
            <TabIntro icon={activePrimary.icon} title={activePrimary.title} description={activePrimary.description} />
            <RegulationsPage lat={selectedSite.lat} lng={selectedSite.lng} siteName={selectedSite.name} />
          </div>
        )}

        {primary === "data" && (
          <div className="animate-ds-fade-in">
            <TabIntro icon={activeDataSub.icon} title={activeDataSub.title} description={activeDataSub.description} />
            <DataWorkspace
              sub={dataSub}
              selectedSite={selectedSite}
              setSelectedSite={setSelectedSite}
              selectedSpecies={selectedSpecies}
              setSelectedSpecies={setSelectedSpecies}
              plume={plume}
              updatePlume={updatePlume}
            />
          </div>
        )}

        {primary === "sources" && (
          <div className="animate-ds-fade-in">
            <TabIntro icon={activePrimary.icon} title={activePrimary.title} description={activePrimary.description} />
            <DataSourcesSection title="Data sources & fact-check" className="mb-6" />
            <p className="text-sm text-fg-secondary">
              Live panels on the Sites view poll on a schedule and show <strong className="font-medium text-fg">Last updated</strong> when serving cached bundles.
            </p>
          </div>
        )}

      </main>

      <AppFooter onContact={() => setContactOpen(true)} />

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
